import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function StatusBadge({ value }) {
  const v = String(value || 'draft');
  const cls =
    v === 'verified'
      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
      : v.includes('suspended')
        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
        : v.includes('rejected')
          ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
          : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${cls}`}>{v}</span>;
}

export default function ProfessionalHub({ requiredType = null, panelTitle = 'Uzman Paneli' }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profileData, setProfileData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [invites, setInvites] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [targetUserId, setTargetUserId] = useState('');
  const [createdLink, setCreatedLink] = useState('');

  const professionalType = useMemo(
    () => String(profileData?.profile?.professionalType || '').toLowerCase(),
    [profileData]
  );
  const normalizedRequiredType = String(requiredType || '').toLowerCase() || null;
  const typeMismatch = Boolean(normalizedRequiredType && professionalType && professionalType !== normalizedRequiredType);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError('');
      const [profileRes, subRes, invitesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/professional/profile`, { headers: authHeaders() }),
        axios.get(`${API_BASE_URL}/api/professional/subscription`, { headers: authHeaders() }),
        axios.get(`${API_BASE_URL}/api/professional/invites`, { headers: authHeaders() }),
      ]);
      setProfileData(profileRes.data || null);
      setSubscriptionData(subRes.data || null);
      setInvites(Array.isArray(invitesRes.data?.items) ? invitesRes.data.items : []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Uzman panel verileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    if (!Number.isInteger(Number(targetUserId))) return;
    try {
      setInviteLoading(true);
      setError('');
      setCreatedLink('');
      const res = await axios.post(
        `${API_BASE_URL}/api/professional/invites`,
        { targetUserId: Number(targetUserId) },
        { headers: authHeaders() }
      );
      const token = res.data?.invite?.inviteToken;
      if (token) {
        const legacyPath = professionalType === 'pt' ? 'pt-invite' : 'dietitian-invite';
        const nextLink = `${window.location.origin}/${legacyPath}/${token}`;
        setCreatedLink(nextLink);
      }
      await loadAll();
      setTargetUserId('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Davet oluşturulamadı.');
    } finally {
      setInviteLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-sm text-slate-500 dark:text-slate-300">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6 p-4 md:p-8">
      <div>
        <h1 className="text-2xl font-black text-slate-900 dark:text-white">{panelTitle}</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Profil, abonelik, kota ve danışan davetlerini buradan yönetin.
        </p>
      </div>

      {error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-300">
          {error}
        </div>
      ) : null}

      {typeMismatch ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
          Bu panel sadece <span className="font-bold">{normalizedRequiredType === 'dietitian' ? 'diyetisyen' : 'PT'}</span> hesabı için erişilebilir.
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Profil Durumu</div>
          <div className="mt-3"><StatusBadge value={profileData?.status} /></div>
          <div className="mt-3 text-sm text-slate-700 dark:text-slate-300">
            {profileData?.profile?.fullName || '-'}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{professionalType || '-'}</div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Abonelik</div>
          <div className="mt-3 text-sm font-semibold text-slate-900 dark:text-white">
            {subscriptionData?.subscription?.plan?.displayName || 'Aktif plan yok'}
          </div>
          <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Kota: {subscriptionData?.quota?.activeRelationshipCount ?? 0} / {subscriptionData?.quota?.maxActiveClientCount ?? 0}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Erişim Modu</div>
          <div className="mt-3 text-sm font-semibold text-slate-900 dark:text-white">
            {subscriptionData?.quota?.accessMode || '-'}
          </div>
          <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Kalan kapasite: {subscriptionData?.quota?.remainingCapacity ?? 0}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <h2 className="text-base font-bold text-slate-900 dark:text-white">Yeni Danışan Daveti</h2>
        <form onSubmit={handleCreateInvite} className="mt-4 flex flex-col gap-3 md:flex-row">
          <input
            type="number"
            min="1"
            value={targetUserId}
            onChange={(e) => setTargetUserId(e.target.value)}
            className="h-11 flex-1 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
            placeholder="Danışan kullanıcı ID"
            disabled={typeMismatch}
          />
          <button
            type="submit"
            disabled={inviteLoading || typeMismatch}
            className="h-11 rounded-xl bg-slate-900 px-4 text-sm font-bold text-white disabled:opacity-60 dark:bg-white dark:text-slate-900"
          >
            {inviteLoading ? 'Oluşturuluyor...' : 'Davet Oluştur'}
          </button>
        </form>
        {createdLink ? (
          <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-300">
            Davet linki: {createdLink}
          </div>
        ) : null}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <h2 className="text-base font-bold text-slate-900 dark:text-white">Davet Geçmişi</h2>
        {invites.length === 0 ? (
          <div className="mt-3 text-sm text-slate-500 dark:text-slate-400">Henüz davet yok.</div>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  <th className="px-3 py-2">Durum</th>
                  <th className="px-3 py-2">Hedef</th>
                  <th className="px-3 py-2">Token</th>
                  <th className="px-3 py-2">Tarih</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((item) => (
                  <tr key={item.id} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="px-3 py-2"><StatusBadge value={item.status} /></td>
                    <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{item.targetUser?.email || item.targetUserId || '-'}</td>
                    <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{item.inviteToken}</td>
                    <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{item.createdAt ? new Date(item.createdAt).toLocaleString('tr-TR') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
